import { Card, Badge } from '@/core/ui/components';
import type { Integration } from '@yawi/types';
import { Plug, Clock, Mail, MessageSquare, Bell, CreditCard } from 'lucide-react';

interface IntegrationsCardProps {
  integrations: Integration[];
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  EMAIL: <Mail className="w-4 h-4" />,
  SMS: <MessageSquare className="w-4 h-4" />,
  WHATSAPP: <MessageSquare className="w-4 h-4" />,
  PUSH: <Bell className="w-4 h-4" />,
  PAYMENT: <CreditCard className="w-4 h-4" />,
};

const STATUS_BADGES: Record<string, { variant: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
  ENABLED: { variant: 'success', label: 'Activo' },
  DISABLED: { variant: 'default', label: 'Inactivo' },
  ERROR: { variant: 'error', label: 'Error' },
};

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Nunca';

  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) return 'Hace un momento';
  if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)}h`;
  if (diff < 604800000) return `Hace ${Math.floor(diff / 86400000)}d`;
  return date.toLocaleDateString();
}

export function IntegrationsCard({ integrations }: IntegrationsCardProps) {
  if (!integrations || integrations.length === 0) {
    return (
      <Card>
        <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
          <Plug className="w-5 h-5" />
          Integraciones
        </h3>
        <p className="text-theme-secondary text-center py-4">
          Sin integraciones configuradas
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
        <Plug className="w-5 h-5" />
        Integraciones ({integrations.length})
      </h3>

      <div className="space-y-3">
        {integrations.map((integration) => {
          const statusBadge = STATUS_BADGES[integration.status] || STATUS_BADGES.ENABLED;
          const typeIcon = TYPE_ICONS[integration.type] || <Plug className="w-4 h-4" />;

          return (
            <div
              key={integration.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50"
            >
              <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                {typeIcon}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-theme truncate">{integration.name}</span>
                  <Badge variant={statusBadge.variant} className="text-xs">
                    {statusBadge.label}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-theme-secondary">
                  <Badge variant="default" className="text-xs">{integration.type}</Badge>
                  <span className="truncate">{integration.provider}</span>
                </div>
              </div>

              <div className="text-right shrink-0">
                <p className="text-xs text-theme-secondary flex items-center gap-1 justify-end">
                  <Clock className="w-3 h-3" />
                  {formatRelativeTime(integration.lastUsedAt)}
                </p>
                {integration.errorCount > 0 && (
                  <p className="text-xs text-red-600 mt-1">
                    {integration.errorCount} errores
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
